import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { RouterProvider } from "react-router";
import { Toaster } from "sonner";
import { api, sessionExpiredEvent } from "../data/api";
import { BudgetStore } from "../data/budgetStore";
import {
  clearSession,
  deviceId,
  readSession,
  sessionKey,
  writeSession,
  type SessionMarker,
} from "../data/session";
import { AppProvider, useApp, useSyncStatus } from "../data/StoreContext";
import { LoginPage } from "../features/auth/LoginPage";
import { Logo } from "../ui";
import { createAppRouter } from "./routes";
import { UpdatePrompt } from "./UpdatePrompt";

export interface AppRootProps {
  /** Tests inject an in-memory store and router. */
  createStore?: (marker: SessionMarker) => BudgetStore;
  router?: ReturnType<typeof createAppRouter>;
  connect?: boolean;
  children?: ReactNode;
}

const defaultCreateStore = (marker: SessionMarker) =>
  new BudgetStore({ userId: marker.userId, deviceId: deviceId() });

export function AppRoot({
  createStore = defaultCreateStore,
  router,
  connect = true,
}: AppRootProps) {
  const [session, setSession] = useState<SessionMarker | null>(() => readSession());
  const [store, setStore] = useState<BudgetStore | null>(null);
  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: { queries: { networkMode: "always" } } }),
    [],
  );
  const appRouter = useMemo(() => router ?? createAppRouter(), [router]);
  const pathname = useSyncExternalStore(
    appRouter.subscribe,
    () => appRouter.state.location.pathname,
  );

  useEffect(() => {
    if (!session) {
      return;
    }
    const s = createStore(session);
    setStore(s);
    // Background sync starts after the first render: nothing on the network blocks startup.
    const t = connect ? setTimeout(() => s.connect(), 0) : undefined;
    if (navigator.storage?.persist) {
      void navigator.storage.persist().catch(() => false);
    }
    return () => {
      clearTimeout(t);
      void s.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.userId]);

  // Another tab logged out (or in as someone else): drop this tab's copy of the old session.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== sessionKey && e.key !== null) {
        return;
      }
      const next = readSession();
      if (next?.userId === session?.userId) {
        return;
      }
      queryClient.clear();
      setStore(null);
      setSession(next);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [session?.userId, queryClient]);

  const logout = useCallback(async () => {
    await api("/auth/logout", { method: "POST" }).catch(() => undefined);
    // Clear the marker first: other tabs then close their copy, which would block the wipe.
    clearSession();
    await store?.wipe();
    queryClient.clear();
    setStore(null);
    setSession(null);
  }, [store, queryClient]);

  const relogin = useCallback(
    (marker: SessionMarker) => {
      if (session && marker.userId !== session.userId) {
        // Someone else logged in: the previous person's local copy goes, as on log out.
        void (async () => {
          writeSession(marker);
          await store?.wipe();
          queryClient.clear();
          setStore(null);
          setSession(marker);
        })();
        return;
      }
      writeSession(marker);
      store?.reconnect();
      void queryClient.invalidateQueries();
    },
    [session, store, queryClient],
  );

  if (!session) {
    return (
      <LoginPage
        onLoggedIn={(marker) => {
          writeSession(marker);
          setSession(marker);
        }}
      />
    );
  }
  if (!store) {
    return <Splash />;
  }
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider value={{ store, session, logout, relogin }}>
        <LoadedGate>
          <ExpiredSessionGate>
            <RouterProvider router={appRouter} />
          </ExpiredSessionGate>
        </LoadedGate>
        <Toaster position="top-center" toastOptions={{ className: "font-sans" }} />
        <UpdatePrompt placement={pathname === "/add" ? "top" : "bottom"} />
      </AppProvider>
    </QueryClientProvider>
  );
}

/** How long to wait for IndexedDB before rendering anyway (it normally loads in milliseconds). */
const loadFallbackMs = 3000;

/**
 * Screens render only once the local copy has loaded, so a deep link doesn't flash "not found"
 * and Quick Add always has its account. If IndexedDB never answers, render anyway.
 */
function LoadedGate({ children }: { children: ReactNode }) {
  const { loaded } = useSyncStatus();
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (loaded) {
      return;
    }
    const t = setTimeout(() => setGaveUp(true), loadFallbackMs);
    return () => clearTimeout(t);
  }, [loaded]);
  return loaded || gaveUp ? <>{children}</> : <Splash />;
}

function Splash() {
  return (
    <div
      role="status"
      aria-label="Loading Budgeter"
      className="grid min-h-dvh place-items-center bg-bg"
    >
      <Logo size={56} />
    </div>
  );
}

/** When the server refuses the session, ask to log in again without discarding anything. */
function ExpiredSessionGate({ children }: { children: ReactNode }) {
  const status = useSyncStatus();
  const [apiExpired, setApiExpired] = useState(false);
  useEffect(() => {
    const onExpired = () => setApiExpired(true);
    window.addEventListener(sessionExpiredEvent, onExpired);
    return () => window.removeEventListener(sessionExpiredEvent, onExpired);
  }, []);
  return (
    <>
      {children}
      {(status.authFailed || apiExpired) && <ReloginOverlay onDone={() => setApiExpired(false)} />}
    </>
  );
}

function ReloginOverlay({ onDone }: { onDone: () => void }) {
  const { relogin } = useApp();
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-bg">
      <LoginPage
        expired
        onLoggedIn={(marker) => {
          relogin(marker);
          onDone();
        }}
      />
    </div>
  );
}
